import React, { useEffect, useMemo, useState } from 'react';
import { Card } from 'primereact/card';
import { Calendar } from 'primereact/calendar';
import { Button } from 'primereact/button';
import { Chart } from 'primereact/chart';
import { productionTimeAPI, ProductionTimeSummary } from '../services/api';
import { showCustomAlert } from './CustomAlert';
import './ProductionTime.css';

const useIsMobile = (breakpoint: number = 768) => {
  const [isMobile, setIsMobile] = useState<boolean>(
    typeof window !== 'undefined' ? window.innerWidth <= breakpoint : false
  );
  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth <= breakpoint);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [breakpoint]);
  return isMobile;
};

const ProductionTimeReports: React.FC = () => {
  const isMobile = useIsMobile();
  const [dateRange, setDateRange] = useState<Date[]>(() => {
    const now = new Date();
    const start = new Date(now.getFullYear(), now.getMonth(), 1);
    const end = new Date(now.getFullYear(), now.getMonth() + 1, 0);
    return [start, end];
  });
  const [summary, setSummary] = useState<ProductionTimeSummary | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (dateRange[0] && dateRange[1]) {
      void loadSummary();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateRange[0]?.toISOString(), dateRange[1]?.toISOString()]);

  const loadSummary = async () => {
    if (!dateRange[0] || !dateRange[1]) return;

    setLoading(true);
    try {
      const startDate = dateRange[0].toISOString().split('T')[0];
      const endDate = dateRange[1].toISOString().split('T')[0];

      const data = await productionTimeAPI.getEntries({
        periodStart: startDate,
        periodEnd: endDate
      });

      setSummary(data.summary as ProductionTimeSummary);
    } catch (err: any) {
      // eslint-disable-next-line no-console
      console.error('Error loading production time summary:', err);
      showCustomAlert.error('Greška pri učitavanju izveštaja');
    } finally {
      setLoading(false);
    }
  };

  const formatDuration = (minutes: number) => {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return `${hours}h ${mins}min`;
  };

  const chartData = useMemo(() => {
    if (!summary) return null;

    const labels: string[] = [];
    const values: number[] = [];
    const colors: string[] = [];

    // Redovan rad
    if (summary.regularMinutes > 0) {
      labels.push('Redovan rad');
      values.push(summary.regularMinutes);
      colors.push('#2563eb');
    }

    // Specijalni modovi
    summary.deductionByState?.forEach((mode, index) => {
      labels.push(mode.stateName);
      values.push(mode.totalMinutes);
      // Koristi boju iz stanja ako postoji, inače koristi paletu
      colors.push(mode.stateColor || ['#a855f7', '#ef4444', '#f97316', '#22c55e', '#06b6d4'][index % 5]);
    });

    return {
      labels,
      datasets: [
        {
          data: values,
          backgroundColor: colors,
          hoverBackgroundColor: colors
        }
      ]
    };
  }, [summary]);

  const chartOptions = useMemo(
    () => ({
      maintainAspectRatio: false,
      responsive: true,
      cutout: '65%',
      plugins: {
        legend: {
          position: 'bottom' as const,
          labels: {
            font: { size: isMobile ? 11 : 13 },
            boxWidth: isMobile ? 12 : 16,
            padding: isMobile ? 8 : 12
          }
        },
        tooltip: {
          callbacks: {
            label: (context: any) => {
              const minutes = context.parsed;
              const hours = Math.floor(minutes / 60);
              const mins = minutes % 60;
              return `${context.label}: ${hours}h ${mins}min`;
            }
          }
        }
      }
    }),
    [isMobile]
  );

  return (
    <div className="ptime-page">
      <div className="ptime-header">
        <h1>{isMobile ? 'Izveštaji vremena' : 'Izveštaji vremena proizvodnje'}</h1>

        <div className="ptime-toolbar">
          <div className="ptime-toolbar-period">
            <label>Period:</label>
            <Calendar
              value={dateRange}
              onChange={(e) => setDateRange(e.value as Date[])}
              selectionMode="range"
              dateFormat="dd.mm.yy"
              showIcon
              readOnlyInput
            />
          </div>

          <div className="ptime-toolbar-actions">
            <Button
              label="Export PDF"
              icon="pi pi-file-pdf"
              onClick={() => showCustomAlert.info('Export PDF će biti implementiran uskoro')}
            />
          </div>
        </div>
      </div>

      {loading && (
        <div className="ptime-cards-empty">
          <i className="pi pi-spin pi-spinner" style={{ fontSize: '1.5rem' }} />
          <div style={{ marginTop: '0.5rem' }}>Učitavanje...</div>
        </div>
      )}

      {!loading && summary && chartData && (
        <div className="ptime-report-grid">
          <Card>
            <h2 style={{ marginTop: 0, fontSize: isMobile ? '1rem' : '1.25rem' }}>
              Raspodela vremena po fazama
            </h2>
            <div
              className="ptime-report-chart"
              style={{ height: isMobile ? '280px' : '440px' }}
            >
              <Chart type="doughnut" data={chartData} options={chartOptions} />
            </div>
            <div className="ptime-report-total">
              <div className="ptime-report-total-label">Ukupno vreme</div>
              <div className="ptime-report-total-value">
                {formatDuration(summary.totalMinutes || 0)}
              </div>
            </div>
          </Card>

          <Card title="Detaljno po fazama">
            <div
              className="ptime-report-detail-card"
              style={{ borderLeftColor: '#2563eb', background: '#eff6ff' }}
            >
              <div className="ptime-report-detail-name">Redovan rad</div>
              <div className="ptime-report-detail-value">
                {formatDuration(summary.regularMinutes || 0)}
              </div>
              <div className="ptime-report-detail-meta">
                Procenat:{' '}
                {(
                  ((summary.regularMinutes || 0) / (summary.totalMinutes || 1)) *
                  100
                ).toFixed(1)}
                %
              </div>
            </div>

            {summary.deductionByState?.map((mode, index) => (
              <div
                key={mode.stateId}
                className="ptime-report-detail-card"
                style={{
                  borderLeftColor: mode.stateColor || '#f97316',
                  background: index % 2 === 0 ? '#fff7ed' : '#fef2ff'
                }}
              >
                <div className="ptime-report-detail-name">{mode.stateName}</div>
                <div className="ptime-report-detail-value">
                  {formatDuration(mode.totalMinutes)}
                </div>
                <div className="ptime-report-detail-meta">
                  Broj zapisa: {mode.entryCount}
                </div>
                <div className="ptime-report-detail-meta">
                  Procenat:{' '}
                  {((mode.totalMinutes / (summary.totalMinutes || 1)) * 100).toFixed(1)}%
                </div>
              </div>
            ))}
          </Card>
        </div>
      )}

      {!loading && (!summary || !chartData) && (
        <div className="ptime-cards-empty">Nema podataka za izabrani period.</div>
      )}
    </div>
  );
};

export default ProductionTimeReports;